import { CategoryScale, ChartData, ChartOptions, Chart as ChartJS, Filler, LineElement, LinearScale, PointElement, Tooltip } from 'chart.js'
import AnnotationPlugin, { AnnotationOptions } from 'chartjs-plugin-annotation'
import { useMemo } from 'react'

import { Theme, alpha } from '@mui/material'

import type { SecondData } from '../../types'
import { DATA_BAND_BOTTOM, MIN_INTENSITY, ROW_BOTTOM_PAD, ROW_TOP_PAD, colorForRow, intensityToDotRadius, intensityToY } from './TimelinePalette'

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, AnnotationPlugin)

const buildChartData = (seconds: SecondData[], motivationKeysInOrder: string[], motivationKeyToLabel: Record<string, string>): ChartData<'line'> => ({
  datasets: motivationKeysInOrder.map((key, rowIndex) => {
    const stroke = colorForRow(rowIndex)
    const fillColor = alpha(stroke, 0.22)
    return {
      label: motivationKeyToLabel[key],
      // `intensity` is carried alongside x/y so the tooltip can show raw 1-5
      // values without reversing the rowIndex offset baked into y.
      // Intensities at or below the row baseline (≤ 1) are treated as "no
      // signal" — y is NaN so no line/fill is rendered through those points
      // (Chart.js skips NaN data points, and `spanGaps` bridges over them
      // when neighbouring rows have real values).
      data: seconds.map((s) => {
        const raw = s.intensity[key]
        const intensity = raw ?? MIN_INTENSITY
        const y = intensity > MIN_INTENSITY ? intensityToY(rowIndex, intensity) : NaN
        return { x: s.second, y, intensity }
      }),
      borderColor: stroke,
      borderWidth: 1.5,
      backgroundColor: fillColor,
      // Fill stops at the data band's bottom (not the integer row boundary)
      // so the shaded area leaves a clear gap before the next row begins.
      fill: { target: { value: rowIndex + DATA_BAND_BOTTOM }, above: fillColor, below: 'transparent' },
      pointRadius: 0,
      pointHoverRadius: 0,
      pointHitRadius: 0,
      tension: 0,
      spanGaps: true,
    }
  }),
})

const buildAnnotations = (
  seconds: SecondData[],
  currentSecond: number | null | undefined,
  hoverSecond: number | null,
  theme: Theme,
  motivationKeysInOrder: string[]
): Record<string, AnnotationOptions> => {
  const activeColor = theme.palette.secondary.main
  const annotations: Record<string, AnnotationOptions> = {}

  const showHover = hoverSecond != null && hoverSecond !== currentSecond
  if (showHover) {
    // Crisp 1.5px line in navy, drawn over the curves so the marker stays
    // unmistakable even when it crosses a pink-filled area band.
    annotations['hover-cursor'] = {
      type: 'line',
      xMin: hoverSecond,
      xMax: hoverSecond,
      borderColor: theme.palette.secondary.main,
      borderWidth: 1.5,
      drawTime: 'afterDatasetsDraw',
    }
    // Filled "pin head" caps at the top and bottom of the plot area — gives
    // the cursor a definite start/end and reads as a Material-style pinned
    // marker rather than a free-floating line.
    annotations['hover-cap-top'] = {
      type: 'point',
      xValue: hoverSecond,
      yValue: 0,
      radius: 3,
      backgroundColor: theme.palette.secondary.main,
      borderWidth: 0,
      drawTime: 'afterDatasetsDraw',
    }
    annotations['hover-cap-bottom'] = {
      type: 'point',
      xValue: hoverSecond,
      yValue: motivationKeysInOrder.length,
      radius: 3,
      backgroundColor: theme.palette.secondary.main,
      borderWidth: 0,
      drawTime: 'afterDatasetsDraw',
    }
  }

  if (currentSecond != null) {
    annotations['active-cursor'] = {
      type: 'line',
      xMin: currentSecond,
      xMax: currentSecond,
      borderColor: activeColor,
      borderWidth: 1,
      borderDash: [2, 2],
      drawTime: 'afterDatasetsDraw',
    }

    const activeSecondData = seconds.find((s) => s.second === currentSecond)
    if (activeSecondData) {
      motivationKeysInOrder.forEach((key, rowIndex) => {
        const intensity = activeSecondData.intensity[key] ?? MIN_INTENSITY
        // Match the dot to its row's line colour so the active-second markers
        // double as a colour legend along the dashed cursor.
        annotations[`active-dot-${rowIndex}`] = {
          type: 'point',
          xValue: currentSecond,
          yValue: intensityToY(rowIndex, intensity),
          radius: intensityToDotRadius(intensity),
          backgroundColor: colorForRow(rowIndex),
          borderColor: theme.palette.background.paper,
          borderWidth: 1,
          drawTime: 'afterDatasetsDraw',
        }
      })
    }
  }

  return annotations
}

interface BuildOptionsArgs {
  seconds: SecondData[]
  currentSecond: number | null | undefined
  hoverSecond: number | null
  theme: Theme
  numRows: number
  motivationKeysInOrder: string[]
}

const buildChartOptions = ({ seconds, currentSecond, hoverSecond, theme, numRows, motivationKeysInOrder }: BuildOptionsArgs): ChartOptions<'line'> => {
  const activeColor = theme.palette.secondary.main
  const lastSecond = seconds.length === 0 ? 0 : seconds[seconds.length - 1].second

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    // Pointer events for hover/click + tooltip handling are owned by the
    // wrapper Box so they fire over the x-axis label strip too, not just the
    // plot area. Chart.js's built-in event plumbing is disabled here on
    // purpose.
    events: [],
    layout: {
      padding: { top: ROW_TOP_PAD, bottom: ROW_BOTTOM_PAD, left: 0, right: 0 },
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      filler: { propagate: false },
      annotation: {
        annotations: buildAnnotations(seconds, currentSecond, hoverSecond, theme, motivationKeysInOrder),
      },
    },
    scales: {
      x: {
        type: 'linear',
        min: 0,
        max: lastSecond,
        offset: false,
        border: { display: false },
        grid: { display: false },
        ticks: {
          stepSize: 1,
          autoSkip: false,
          color: (ctx) => (ctx.tick && ctx.tick.value === currentSecond ? activeColor : theme.palette.text.disabled),
          font: (ctx) => ({
            size: 10,
            weight: ctx.tick && ctx.tick.value === currentSecond ? 'bold' : 'normal',
          }),
        },
      },
      y: {
        type: 'linear',
        min: 0,
        max: numRows,
        reverse: true,
        border: { display: false },
        ticks: { display: false },
        grid: {
          display: true,
          // Use a callback to colour only the integer (row boundary) lines —
          // Chart.js may emit a sub-row tick when auto-stepping; we ignore
          // those by returning a transparent stroke.
          color: (ctx) => {
            const value = ctx.tick?.value ?? 0
            return Number.isInteger(value) ? theme.palette.divider : 'transparent'
          },
          lineWidth: 1,
          drawTicks: false,
        },
      },
    },
  }
}

interface UseTimelineChartDataArgs {
  seconds: SecondData[]
  currentSecond: number | null | undefined
  hoverSecond: number | null
  theme: Theme
  numRows: number
  motivationKeysInOrder: string[]
  motivationKeyToLabel: Record<string, string>
}

/**
 * Memoised Chart.js dataset + options + annotations payload for the
 * {@link MotivationTimeline} renderer. Re-derives whenever any of the
 * timeline's inputs change so the rest of the component can render the
 * underlying `<Line>` without owning Chart.js wiring.
 */
export const useTimelineChartData = ({
  seconds,
  currentSecond,
  hoverSecond,
  theme,
  numRows,
  motivationKeysInOrder,
  motivationKeyToLabel,
}: UseTimelineChartDataArgs) => {
  const data = useMemo(() => buildChartData(seconds, motivationKeysInOrder, motivationKeyToLabel), [seconds, motivationKeysInOrder, motivationKeyToLabel])
  const options = useMemo(
    () => buildChartOptions({ seconds, currentSecond, hoverSecond, theme, numRows, motivationKeysInOrder }),
    [seconds, currentSecond, hoverSecond, theme, numRows, motivationKeysInOrder]
  )
  return { data, options }
}
